import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { FormattedMessage, injectIntl } from 'react-intl';
import { MoveFocusInside } from 'react-focus-lock';
import gql from 'graphql-tag';
import { fromGlobalId } from 'graphql-relay';
import { graphql } from '@apollo/react-hoc';
import { SimpleLoadingComponent } from '../../utils/apollo';
import Button from '../../components/Button';
import Divider from '../../components/Divider';
import Header from '../../components/Header';
import Icon from '../../components/Icon';
import Loader from '../../components/Loader';
import Modal from '../../components/Modal';
import Message from '../../components/Message';
import Grid from '../../components/Grid';
import Toast, { ToastKind } from '../../components/Toast';
import { cancellationPermitted, isVisitFuture, prepareRender, getAppointmentFromProps, getStartingState } from './util';
import updateRsvpRepVisitMutation from '../../mutations/HighSchool/updateRsvpRepVisit';
import cancelRsvpRepVisitMutation from '../../mutations/HighSchool/cancelRsvpRepVisit';
import EventDetails from './event-details';
import EventVisitUrl from './event-visit-url';
import InternalNotes from './event-notes';
import EventRescheduleIntl from './event-reschedule';
import EventVisitCancel from './event-visit-cancel';
import messages from './messages';
import HighSchoolDetailsDisplay from '../HighSchoolDetailsDisplay';
import REPVISITS_LOCATION_TYPE from '../../utils/repvisits-location-types';
import { isUrlValid, addProtocol } from '../../utils/url';
import './styles.css';

class EventVisitDetailsComponent extends React.PureComponent {
  static propTypes = {
    node: PropTypes.object,
    appointmentId: PropTypes.string.isRequired,
    onSave: PropTypes.func.isRequired,
    mutateRsvpRepVisit: PropTypes.func.isRequired,
    cancelRsvpRepVisit: PropTypes.func.isRequired,
    dismissNotification: PropTypes.func,
    onlyVisitUrl: PropTypes.bool,
    notificationId: PropTypes.string,
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
  };

  cancelSuccessNotification = (
    <Toast
      active
      title=""
      description={this.props.intl.formatMessage(messages.cancellationSuccess)}
      kind={ToastKind.success}
    />
  );

  cancelFailureNotification = (
    <Toast
      active
      title=""
      description={this.props.intl.formatMessage(messages.cancellationFail)}
      kind={ToastKind.error}
    />
  );

  saveSuccessNotification = (
    <Toast
      active
      title={this.props.intl.formatMessage(messages.saveSuccessTitle)}
      description={this.props.intl.formatMessage(messages.saveSuccessDescription)}
      kind={ToastKind.success}
    />
  );

  saveFailureNotification = (
    <Toast
      active
      title=""
      description={this.props.intl.formatMessage(messages.saveFail)}
      kind={ToastKind.error}
    />
  );

  constructor(props, context) {
    super(props, context);
    this.state = getStartingState(props);
  }

  componentWillReceiveProps(nextProps) {
    const currentAppointment = getAppointmentFromProps(nextProps);
    const notes = get(currentAppointment, 'notes', '');
    const visitUrl = get(currentAppointment, 'visitUrl', '');
    this.setState({ notes, visitUrl });

    if (this.props.notificationId !== nextProps.notificationId) {
      this.setState(getStartingState(this.props));
    }
    if (this.props.appointmentId !== nextProps.appointmentId) {
      this.setState({
        currentAppointment,
        cancelling: false,
        reschedule: false,
      });
    }
  }

  onChange = (event) => {
    this.setState({ notes: event.target.value });
  };

  onVisitUrlChange = (event) => {
    const visitUrl = event.target.value;
    this.setState({ visitUrl, visitUrlError: false });
  }

  beforeSave = (result) => {
    this.setState(getStartingState(this.props));
    this.props.onSave(result);
  }

  onClose = () => this.beforeSave(null);

  onSaveSuccess = () => this.beforeSave(this.saveSuccessNotification);

  onSaveFailure = () => this.beforeSave(this.saveFailureNotification);

  onCancelSuccess = () => this.beforeSave(this.cancelSuccessNotification);

  onCancelFailure = () => this.beforeSave(this.cancelFailureNotification);

  cancelVisit = (cancellationMessage) => {
    const { node, appointmentId, cancelRsvpRepVisit } = this.props;
    this.setState({ executingCancellation: true });

    cancelRsvpRepVisit({
      variables: {
        input: {
          cancellationMessage,
          repVisit: {
            dayId: node.id,
            appointmentId,
          },
        },
      },
    }).then(this.onCancelSuccess, this.onCancelFailure);
  };

  toggleCancelling = () => {
    this.setState((prevState) => ({ cancelling: !prevState.cancelling }));
  };

  toggleReschedule = () => this.setState((prevState) => ({ reschedule: !prevState.reschedule }));

  mutateRepVisit = () => {
    const { notes, visitUrl } = this.state;
    const { node, appointmentId, mutateRsvpRepVisit } = this.props;
    return mutateRsvpRepVisit({
      variables: {
        input: {
          notes: notes || '',
          visitUrl: addProtocol(visitUrl).slice(0, 2048),
          repVisit: {
            dayId: node.id,
            appointmentId,
          },
        },
      },
    });
  }

  save = () => {
    const { visitUrl } = this.state;
    const currentAppointment = getAppointmentFromProps(this.props);
    const chosenVisitLocationType = get(currentAppointment, 'chosenVisitLocationType');
    const isVirtualEvent = chosenVisitLocationType === REPVISITS_LOCATION_TYPE.VIRTUAL;
    if (isVirtualEvent && (!visitUrl || !isUrlValid(visitUrl))) {
      return this.setState({ visitUrlError: true });
    }
    return this.mutateRepVisit().then(this.onSaveSuccess).catch(this.onSaveFailure);
  };

  openHighSchoolDetails = (highSchoolId) => this.setState({ highSchoolId });

  closeHighSchoolDetails = () => this.setState({ highSchoolId: null });

  onConfirmVirtualUrl = () => {
    const { visitUrl } = this.state;
    if (!visitUrl || !isUrlValid(visitUrl)) {
      return this.setState({ visitUrlError: true });
    }

    return this.mutateRepVisit().then(() => {
      this.props.dismissNotification(this.props.notificationId);
      this.setState({ visitUrlSaved: true, loadingVisitUrlConfirmation: false });
    }).catch((error) => {
      // if visit no longer exists, auto-dismiss the stale notification instead of showing error
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || '';
      if (errorMessage.includes('RepVisit is not available')) {
        this.props.dismissNotification(this.props.notificationId);
        this.setState({ visitUrlSaved: true, loadingVisitUrlConfirmation: false });
      } else {
        this.setState({ visitUrlSaveFailure: true, loadingVisitUrlConfirmation: false });
      }
    });
  }

  renderVisitUrlConfirmation = () => {
    const { eventDetails: { title }, highSchool, visitUrlSaved } = prepareRender(this.props, this.state);
    if (!visitUrlSaved) return null;
    return (
      <div styleName="sidebar visit-url-added-container">
        <Icon styleName="visit-url-added-icon" name="check green huge icon" />
        <div styleName="visit-url-added-message">
          <FormattedMessage {...messages.visitUrlAddedPart1} />
          <button
            data-cy="high-school-details-button"
            type="button"
            styleName="highschool-link"
            onClick={() => this.openHighSchoolDetails(highSchool.id && fromGlobalId(highSchool.id).id)}
          >
            {title}
          </button>
          <FormattedMessage {...messages.visitUrlAddedPart2} />
        </div>
        <Button basic onClick={this.onClose}>
          <FormattedMessage {...messages.close} />
        </Button>
      </div>
    );
  }

  renderVisitUrl = () => {
    const { eventDetails: { instructions }, onlyVisitUrl, visitUrlSaved, visitCreator } = prepareRender(this.props, this.state);
    if (!onlyVisitUrl || visitUrlSaved) return null;
    return (
      <div styleName="sidebar">
        <Header as="h1" styleName="header-text visit-url-header">
          <FormattedMessage {...messages.addVisitUrlTitle} />
        </Header>
        <Grid.Row styleName="visit-url-special-instructions">
          <Grid.Column>
            <h5 styleName="detail-label">
              <FormattedMessage {...messages.instructions} />
            </h5>
            <div>
              {instructions}
            </div>
          </Grid.Column>
        </Grid.Row>
        <EventVisitUrl
          visitUrl={this.state.visitUrl}
          visitUrlError={this.state.visitUrlError}
          onVisitUrlChange={this.onVisitUrlChange}
          visitCreator={visitCreator}
        />
        {this.state.visitUrlSaveFailure && (
          <Message error>
            <FormattedMessage {...messages.addVisitUrlError} />
          </Message>
        )}
        <div styleName="visit-url-button-container">
          <Loader inline active={this.state.loadingVisitUrlConfirmation} />
          <Button data-cy="confirm-button" onClick={this.onConfirmVirtualUrl} primary>
            <FormattedMessage {...messages.confirm} />
          </Button>
        </div>
      </div>
    );
  }

  renderCancelling = () => {
    const { node, executingCancellation, eventDetails, onlyVisitUrl, cancelling } = prepareRender(this.props, this.state);
    if (!cancelling || onlyVisitUrl) return null;
    return (
      <div styleName="sidebar">
        <EventVisitCancel
          title={<FormattedMessage {...messages.visitDetails} />}
          cancelButtonLabel={<FormattedMessage {...messages.confirmCancellation} />}
          onCancel={this.cancelVisit}
          goBack={this.toggleCancelling}
          highSchool={get(node, 'highSchool')}
          eventDetails={eventDetails}
          loading={executingCancellation}
        />
      </div>
    );
  }

  renderReschedule = () => {
    const { node, reschedule, cancelling, startDay, highSchool, onlyVisitUrl } = prepareRender(this.props, this.state);
    if (cancelling || !reschedule || onlyVisitUrl) return null;
    return (
      <EventRescheduleIntl
        highSchool={highSchool}
        dayId={node.id}
        appointmentId={this.props.appointmentId}
        defaultDate={startDay}
        onReschedule={this.beforeSave}
        toggleReschedule={this.toggleReschedule}
      />
    );
  }

  renderFutureVisit = () => {
    const { deadline, eventDate, onlyVisitUrl } = prepareRender(this.props, this.state);
    if (!isVisitFuture(eventDate) || onlyVisitUrl) return null;
    return (
      <div styleName="reschedule-and-cancel">
        <h3 styleName="section-title">
          <FormattedMessage {...messages.rescheduleAndCancel} />
        </h3>

        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <h5>
                <FormattedMessage {...messages.reschedule} />
              </h5>
              {cancellationPermitted(eventDate, deadline) ? (
                <div styleName="grey-text" data-cy="repvisits-sidebar-rescheduling-message">
                  <div>
                    <FormattedMessage {...messages.reschedulePolicy} values={{ days: deadline }} />
                  </div>
                  {' '}
                  <Button
                    onClick={this.toggleReschedule}
                    basic
                    styleName="greentext"
                    data-cy="repvisits-sidebar-reschedule-btn"
                  >
                    <FormattedMessage {...messages.rescheduleLink} />
                  </Button>
                </div>
              ) : (
                <div styleName="grey-text">
                  <FormattedMessage {...messages.rescheduleDeadlinePassed} />
                </div>
              )}
            </Grid.Column>
            <Grid.Column width={8}>
              <div styleName="cancel-area">
                <h5>
                  <FormattedMessage {...messages.cancellation} />
                </h5>
                {cancellationPermitted(eventDate, deadline) ? (
                  <div styleName="grey-text">
                    <div>
                      <FormattedMessage {...messages.cancellationDetails} values={{ numDays: deadline }} />
                    </div>
                    <Button
                      onClick={this.toggleCancelling}
                      negative
                      data-cy="repvisits-sidebar-toggle-cancel-btn"
                    >
                      <FormattedMessage {...messages.cancellationLink} />
                    </Button>
                  </div>
                ) : (
                  <div styleName="grey-text">
                    <FormattedMessage {...messages.cancelDeadlinePassed} />
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }

  renderDetails = () => {
    const {
      notes,
      highSchool,
      eventDetails,
      isVirtualEvent,
      onlyVisitUrl,
      cancelling,
      reschedule,
      visitCreator } = prepareRender(this.props, this.state);
    if (onlyVisitUrl || cancelling || reschedule) return null;

    return (
      <div styleName="sidebar" data-cy="event-details-sidebar">
        <EventDetails type="visit" highSchool={highSchool} eventDetails={eventDetails} isVirtualEvent={isVirtualEvent} />
        {!!isVirtualEvent && (
          <EventVisitUrl
            visitUrl={this.state.visitUrl}
            visitUrlError={this.state.visitUrlError}
            onVisitUrlChange={this.onVisitUrlChange}
            visitCreator={visitCreator}
          />
        )}
        <MoveFocusInside>
          <InternalNotes onChange={this.onChange} hsNotes={notes} />
        </MoveFocusInside>
        {this.renderFutureVisit()}
        <Divider section />
        <Button data-cy="save-button" onClick={this.save} floated="right" primary>
          <FormattedMessage {...messages.save} />
        </Button>
      </div>
    );
  }

  renderHighSchoolDetails = () => (
    <Modal
      open={this.state.highSchoolId}
      onClose={this.closeHighSchoolDetails}
      basic
      closeIcon={<Icon name="close" styleName="details-modal-close" />}
      styleName="details-modal"
    >
      <HighSchoolDetailsDisplay id={this.state.highSchoolId} />
    </Modal>
  )

  render() {
    return (
      <React.Fragment>
        {this.renderHighSchoolDetails()}
        {this.renderVisitUrl()}
        {this.renderVisitUrlConfirmation()}
        {this.renderCancelling()}
        {this.renderReschedule()}
        {this.renderDetails()}
      </React.Fragment>
    );
  }
}

export const EventVisitDetails = injectIntl(EventVisitDetailsComponent);

const query = gql`
  query EventVisit($id: ID!) {
    node(id: $id) {
      id
      ... on RepVisitsDay {
        timezone
        date
        appointments {
          id
          notes
          startISOTime
          endISOTime
          chosenVisitLocationType
          visitUrl
          visitCreator
        }
        highSchool {
          id
          name
          interestedStudents
          address {
            field_hs_elna_address_line1
            field_hs_elna_state_latest
            field_hs_elna_city
            field_hs_eln_zip
          }
          repVisitsAccount {
            id
            webInstructions
            primaryContactPhone
            primaryContact {
              id
              name
              field_job_position
              field_contact_work
              field_profile_picture_file_url
            }
            collegeFairsPrimaryContact {
              id
              name
              field_job_position
              field_contact_work
              field_profile_picture_file_url
            }
            collegeFairsPrimaryContactPhone
            availabilitySettings {
              modifyDeadline
            }
          }
        }
      }
    }
  }
`;

const graphqlData = graphql(query, {
  options: ({ id }) => ({
    variables: {
      id,
    },
    fetchPolicy: 'network-only',
  }),
});
const mutateRsvpRepVisit = graphql(updateRsvpRepVisitMutation, {
  name: 'mutateRsvpRepVisit',
  options: { refetchQueries: ['NotificationCount'] },
});

const cancelRsvpRepVisit = graphql(cancelRsvpRepVisitMutation, {
  name: 'cancelRsvpRepVisit',
  options: {
    refetchQueries: [
      'RepVisitsOverview',
      'AvailabilityCalendar',
      'RepVisitsTravelPlanSearch',
      'RepVisitsEvents',
      'EventsAndNotifications',
      'NotificationCount',
    ],
  },
});

export default SimpleLoadingComponent(
  graphqlData,
  mutateRsvpRepVisit,
  cancelRsvpRepVisit,
)(EventVisitDetails);
